import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  useMediaQuery,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { ChevronLeft, FileEdit } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import type { ParentTicket, TicketUpdate } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { useParentTicketDetailForm } from '@/hooks/useParentTicketDetailForm'
import { PageHeader } from '@/components/common/PageHeader'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

import Timeline, { type TimelineItem } from '@/components/helpdesk/TicketDetail/Timeline'
import TicketProperties from '@/components/helpdesk/TicketDetail/TicketProperties'
import StatusSlaCard from '@/components/helpdesk/TicketDetail/StatusSlaCard'
import TicketUpdateModal from '@/components/helpdesk/TicketDetail/TicketUpdateModal'

type Option = { label: string; value: string }

function computeNeedsDetailFetch(ticket: ParentTicket | undefined): boolean {
  if (!ticket) return true
  const m = ticket as ParentTicketWithMeta
  const hasFullRequest = Boolean(m.request)
  const linkedToStore =
    m.store_id != null || (m.store_code != null && String(m.store_code).trim() !== '')
  const hasFullStore = !linkedToStore || Boolean(m.store)
  return !hasFullRequest || !hasFullStore
}

function formatSlaDue(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return iso
  }
}

export default function ParentTicketViewPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const ticket = useParentTicketStore((state) =>
    id ? state.tickets.find((t) => String(t.id) === String(id)) : undefined
  )
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const meta = ticket as ParentTicketWithMeta | undefined
  const needsDetailFetch = useMemo(() => computeNeedsDetailFetch(ticket), [ticket])
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [statusOptions, setStatusOptions] = useState<Option[]>([])
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure()
  const { showSuccess, showError } = useToastNotification()
  const [ticketUpdates, setTicketUpdates] = useState<TicketUpdate[]>([])
  const [editingUpdate, setEditingUpdate] = useState<TicketUpdate | null>(null)
  const [feScheduleRefreshing, setFeScheduleRefreshing] = useState(false)
  const [saveStatusLoading, setSaveStatusLoading] = useState(false)

  const leftColRef = useRef<HTMLDivElement>(null)
  const [leftColHeightPx, setLeftColHeightPx] = useState<number | null>(null)
  const [isXl] = useMediaQuery('(min-width: 1280px)', { ssr: false })

  useLayoutEffect(() => {
    if (!isXl) {
      setLeftColHeightPx(null)
      return
    }
    const el = leftColRef.current
    if (!el) return

    const sync = () => setLeftColHeightPx(el.offsetHeight)
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isXl, id, fetching])

  const fetchTicketUpdates = useCallback(async () => {
    if (!id) return
    const res = await TicketService.getTicketUpdates(id)
    if (res.success && res.data) {
      setTicketUpdates(res.data.data ?? [])
    }
  }, [id])

  const { form, setField } = useParentTicketDetailForm(ticket)

  const ticketNumberDisplay = (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number
  const subjectDisplay = (meta as ParentTicketWithMeta & { subject?: string })?.subject

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<{ data: Array<{ id: number; code: string; label?: string | null }> }>(
        '/service-catalog/parent-ticket-statuses',
        { params: { per_page: 100 } }
      )
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          setStatusOptions(
            items.map((s) => ({
              label: s.label || s.code,
              value: s.code,
            }))
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    fetchTicketUpdates()
  }, [fetchTicketUpdates])

  useEffect(() => {
    if (!id) return
    if (!needsDetailFetch) return

    const ac = new AbortController()
    setFetchError(null)
    setFetching(true)

    TicketService.getParentTicket(id, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        if (res.success && res.data) {
          mergeTicket(res.data)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || axios.isCancel(err)) return
        setFetchError('Failed to load ticket')
      })
      .finally(() => {
        setFetching(false)
      })

    return () => {
      ac.abort()
      setFetching(false)
    }
  }, [id, needsDetailFetch, mergeTicket])

  const formatTimelineDate = (iso: string) =>
    new Date(iso).toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

  const timelineItems: TimelineItem[] = useMemo(() => {
    const sorted = [...ticketUpdates].sort((a, b) => {
      const da = new Date(a.createdAt ?? a.created_at).getTime()
      const db = new Date(b.createdAt ?? b.created_at).getTime()
      return db - da
    })
    return sorted.map((u) => {
      const createdAt = u.createdAt ?? u.created_at
      return {
        id: `update-${u.id}`,
        actor: u.author?.full_name ?? 'Agent',
        action: u.content,
        timeLabel: `${formatTimelineDate(createdAt)} · by ${u.author?.full_name ?? 'Agent'}`,
        type: 'update' as const,
        onEdit: () => setEditingUpdate(u),
      }
    })
  }, [ticketUpdates])

  if (!id) {
    return null
  }

  if (fetching) {
    return (
      <Box
        bg={colors.bg}
        p={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="240px"
      >
        <Spinner size="lg" thickness="3px" color={colors.primary} />
        <Text ml={3} color={colors.textSecondary} fontWeight="500">
          Loading ticket…
        </Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>{fetchError}</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>Parent ticket not found.</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  const storeData = meta?.store
  const requestData = meta?.request

  const ticketWithStoreCode = ticket as typeof ticket & { store_code?: string }
  const storeCode = meta?.store_code || ticketWithStoreCode.store_code || storeData?.storeCode

  const statusSlaSection = (
    <Box flexShrink={0} w="100%" maxW={{ base: '100%', md: '420px', xl: '100%' }} alignSelf={{ base: 'flex-start', xl: 'stretch' }}>
      <StatusSlaCard
        statusLabel={form.status}
        slaDueAt={ticket.slaDueAt ?? null}
        statusValue={form.status}
        statusOptions={statusOptions}
        totalTicketTime={(meta as ParentTicketWithMeta & { total_ticket_time?: string | null })?.total_ticket_time ?? null}
        onChangeStatus={(value) => setField('status', value as ParentTicket['status'])}
        onUpdateStatus={async () => {
          const prevLabel = statusOptions.find((o) => o.value === ticket.status)?.label || ticket.status
          const newLabel = statusOptions.find((o) => o.value === form.status)?.label || form.status
          setSaveStatusLoading(true)
          try {
            await apiClient.patch(`/tickets/${id}`, { status: form.status })
            mergeTicket({ ...ticket, status: form.status } as ParentTicket)
            showSuccess('Status updated')
            await TicketService.createTicketUpdate(id!, {
              content: `Status changed from "${prevLabel}" to "${newLabel}"`,
              type: 'status_change',
              is_internal: true,
            })
            await fetchTicketUpdates()
          } catch (err) {
            showError(err as Error)
          } finally {
            setSaveStatusLoading(false)
          }
        }}
        isSavingStatus={saveStatusLoading}
        isReadOnly={false}
        hidePriority={true}
        embedded
      />
    </Box>
  )

  const activityLogSection = (
    <Box
      flex={{ base: 'none', xl: 1 }}
      minW={0}
      minH={{ xl: 0 }}
      w="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Timeline
        items={timelineItems}
        embedded
        minH={{ base: '220px', md: '260px', xl: 0 }}
        flex={{ xl: 1 }}
        headerAction={
          <Button
            leftIcon={<FileEdit size={14} />}
            variant="solid"
            colorScheme="blue"
            size="xs"
            onClick={onUpdateOpen}
          >
            Add update
          </Button>
        }
      />
    </Box>
  )

  return (
    <Box
      bg={colors.bg}
      p={{ base: 3, md: 4 }}
      pb={8}
      w="100%"
      minW={0}
      minH="calc(100vh - 80px - 48px)"
      display="flex"
      flexDir="column"
    >
      <PageHeader
        mb={3}
        start={
          <Button
            variant="ghost"
            leftIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/tickets')}
            px={2}
            color={colors.text}
            _hover={{ bg: colors.sidebarHoverBg }}
          >
            Back to tickets
          </Button>
        }
      />

      <Card
        variant="outline"
        w="100%"
        bg={colors.cardBg}
        borderColor={colors.border}
        flex="1"
        minW={0}
        shadow="sm"
        overflow="hidden"
      >
        <CardBody p={0}>
          {/* Hero */}
          <Box
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            bg={colors.sectionBg ?? colors.cardBg}
            borderBottom="1px solid"
            borderColor={colors.borderLight}
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap={4}
              align={{ base: 'flex-start', lg: 'flex-start' }}
              justify="space-between"
            >
              <Box minW={0} flex="1">
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color={colors.textMuted}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  mb={1.5}
                >
                  Parent ticket
                </Text>
                <HStack spacing={2} flexWrap="wrap" align="flex-start" mb={1}>
                  <Heading size="md" fontWeight="700" color={colors.heading} noOfLines={3} lineHeight="snug">
                    {subjectDisplay || `Ticket #${ticketNumberDisplay ?? id}`}
                  </Heading>
                  <Badge colorScheme="blue" fontSize="0.7rem" px={2} py={0.5} borderRadius="md" textTransform="none">
                    {ticket.status}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color={colors.textSecondary}>
                  <Text as="span" fontWeight="600" color={colors.text}>
                    {ticketNumberDisplay ?? id}
                  </Text>
                  {ticket.clientName ? ` · ${ticket.clientName}` : null}
                </Text>
                {ticket.slaDueAt ? (
                  <Text fontSize="xs" color={colors.textMuted} mt={2}>
                    SLA target: {formatSlaDue(ticket.slaDueAt)}
                  </Text>
                ) : null}
              </Box>
              <Wrap spacing={2} justify={{ base: 'flex-start', lg: 'flex-end' }}>
                {ticket.clientOrg ? (
                  <WrapItem>
                    <Badge variant="subtle" colorScheme="gray" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                      {ticket.clientOrg}
                    </Badge>
                  </WrapItem>
                ) : null}
                {storeCode ? (
                  <WrapItem>
                    <Badge variant="subtle" colorScheme="purple" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                      Store {storeCode}
                    </Badge>
                  </WrapItem>
                ) : null}
                {ticket.severity ? (
                  <WrapItem>
                    <Badge variant="outline" colorScheme="orange" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                      Priority {String(ticket.severity)}
                    </Badge>
                  </WrapItem>
                ) : null}
              </Wrap>
            </Flex>
          </Box>

          <Grid
            templateColumns={{
              base: '1fr',
              /** Left: case details fill remaining space. Right: fixed-width actions / activity rail. */
              xl: 'minmax(0, 1fr) minmax(360px, 420px)',
              '2xl': 'minmax(0, 1fr) 440px',
            }}
            columnGap={{ xl: 0 }}
            rowGap={{ base: 0, xl: 0 }}
            alignItems="start"
            w="100%"
          >
            <GridItem ref={leftColRef} minW={0} maxW="100%" px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
              <TicketProperties
                request={requestData}
                ticketId={id}
                ticket={{
                  ticketNumber: (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number ?? undefined,
                  description: ticket.description,
                  notes: (meta as ParentTicketWithMeta & { notes?: string })?.notes ?? undefined,
                  workPermit:
                    (meta as ParentTicketWithMeta & { workPermit?: string; work_permit?: string })?.workPermit ??
                    (meta as ParentTicketWithMeta & { work_permit?: string })?.work_permit ??
                    undefined,
                  workPermitPath:
                    (meta as ParentTicketWithMeta & { work_permit_path?: string; workPermitPath?: string })
                      ?.work_permit_path ??
                    (meta as ParentTicketWithMeta & { workPermitPath?: string })?.workPermitPath ??
                    undefined,
                  scheduledAt:
                    (meta as ParentTicketWithMeta & { scheduledAt?: string })?.scheduledAt ??
                    (ticket as ParentTicket & { scheduledAt?: string })?.scheduledAt ??
                    undefined,
                  assets: ((meta as Record<string, unknown>)?.assets ?? undefined) as
                    | { id: number; tag_number?: string | null; model?: string | null }[]
                    | undefined,
                  source: (meta as ParentTicketWithMeta & { source?: string })?.source ?? undefined,
                  severity: ticket.severity,
                  priority: ticket.severity,
                  supportType: (meta as ParentTicketWithMeta & { support_type?: string })?.support_type ?? undefined,
                  subject: (meta as ParentTicketWithMeta & { subject?: string })?.subject ?? undefined,
                  status: ticket.status,
                }}
                storeId={storeData?.storeId ?? meta?.store_id ?? null}
                storeCode={storeCode ?? undefined}
                clientCode={storeData?.clientCode ?? requestData?.clientCode ?? undefined}
                primaryEngineer={meta?.primaryEngineer}
                backupEngineer={meta?.backupEngineer}
                storeDetails={
                  storeData
                    ? {
                        businessUnitName: storeData.businessUnitName ?? null,
                        storeName: storeData.storeName ?? null,
                        branchCode: storeCode ?? storeData.storeCode ?? storeData.branchCode ?? null,
                        contactPerson: storeData.contactPerson ?? null,
                        email: storeData.storeEmail ?? null,
                        contactNumber: storeData.storeContact ?? storeData.contactNumber ?? null,
                        storeAddress: storeData.branchAddress ?? storeData.locationAddress ?? null,
                      }
                    : null
                }
                onWorkPermitUpload={async (ticketId, file) => {
                  try {
                    const fd = new FormData()
                    fd.append('_method', 'PUT')
                    fd.append('workPermitFile', file)
                    await apiClient.post(`/tickets/${ticketId}`, fd)
                    const res = await TicketService.getParentTicket(ticketId)
                    if (res.success && res.data) mergeTicket(res.data)
                    showSuccess('Work permit uploaded')
                    fetchTicketUpdates()
                  } catch (err) {
                    showError(err as Error)
                  }
                }}
                onSchedulesChange={async () => {
                  await fetchTicketUpdates()
                  setFeScheduleRefreshing(true)
                  try {
                    const res = await TicketService.getParentTicket(id!)
                    if (res.success && res.data) mergeTicket(res.data)
                  } finally {
                    setFeScheduleRefreshing(false)
                  }
                }}
                feScheduleRefreshing={feScheduleRefreshing}
                onNotesUpdate={async () => {
                  if (!id) return
                  const res = await TicketService.getParentTicket(id)
                  if (res.success && res.data) mergeTicket(res.data)
                }}
                onPartsChange={fetchTicketUpdates}
                isReadOnly={false}
                embedded
              />
            </GridItem>

            <GridItem
              minW={0}
              minH={0}
              px={{ base: 4, md: 5 }}
              py={{ base: 4, md: 5 }}
              bg={{ base: 'transparent', xl: colors.bg }}
              borderTopWidth={{ base: '1px', xl: 0 }}
              borderTopColor={colors.borderLight}
              borderLeftWidth={{ base: 0, xl: '1px' }}
              borderLeftColor={colors.borderLight}
              display="flex"
              flexDirection="column"
              gap={5}
              overflow={isXl ? 'hidden' : 'visible'}
              h={isXl && leftColHeightPx != null ? `${leftColHeightPx}px` : undefined}
              alignSelf={isXl && leftColHeightPx != null ? 'start' : undefined}
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                color={colors.textSecondary}
                textTransform="uppercase"
                letterSpacing="wider"
                mb={0}
                display={{ base: 'block', xl: 'none' }}
              >
                Actions & activity
              </Text>
              {statusSlaSection}
              {activityLogSection}
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <TicketUpdateModal
        isOpen={isUpdateOpen || editingUpdate !== null}
        onClose={() => {
          onUpdateClose()
          setEditingUpdate(null)
        }}
        ticketId={id!}
        ticketNumber={(meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number}
        onSubmitted={fetchTicketUpdates}
        editingUpdate={editingUpdate}
      />
    </Box>
  )
}
